import React, { useState, useEffect, useRef, useCallback } from 'react';

/*
  SpectatorGameOverlay — read-only game view for stream VIEWERS.
  Covers ALL live games: Spades, Checkers, 4-In-A-Row, Crazy Eights.

  Mount inside the WatchStreamPage video container (a `relative` parent):

    {stream?.game_id && !streamEnded && (
      <SpectatorGameOverlay gameId={stream.game_id} viewerId={getOrCreateVisitorId()} />
    )}

  Data sources (read-only, never sends game actions):
    - GET  /api/game/{gameId}/spectate           → sanitized snapshot on mount
    - WS   /api/ws/game/{gameId}?user_id=spectator_{viewerId}
        spades:        card_played / bid_submitted / trick_complete /
                       turn_change / game_state_update (public fields only)
        checkers:      checkers_state  (full public board)
        crazy eights:  c8_state        (hand counts + discard top — no hands)
        4-in-a-row:    four_in_a_row_move / four_in_a_row_game_over
  Spectators NEVER see any player's hand.
*/

const SUIT_CHAR = { S: '♠', H: '♥', D: '♦', C: '♣', s: '♠', h: '♥', d: '♦', c: '♣' };
const RED_SUITS = ['♥', '♦'];

function parseCard(card) {
  // Accepts "AS", "10H", {rank:'A',suit:'S'}, {rank:'A',suit:'♠'} — defensive.
  if (!card) return null;
  if (typeof card === 'object') {
    const suit = SUIT_CHAR[card.suit] || card.suit || '';
    return { rank: String(card.rank ?? card.value ?? '?'), suit };
  }
  const s = String(card);
  const suit = SUIT_CHAR[s.slice(-1)] || '';
  return { rank: s.slice(0, -1) || s, suit };
}

function MiniCard({ card, size = 'md' }) {
  const p = parseCard(card);
  if (!p) return null;
  const red = RED_SUITS.includes(p.suit);
  const cls = size === 'lg' ? 'w-12 h-16 text-lg' : 'w-8 h-11 text-xs';
  return (
    <div className={`${cls} bg-white rounded-md shadow-md flex flex-col items-center justify-center font-bold ${red ? 'text-red-600' : 'text-gray-900'}`}>
      <span>{p.rank}</span>
      <span className="leading-none">{p.suit}</span>
    </div>
  );
}

export default function SpectatorGameOverlay({ gameId, viewerId }) {
  const [open, setOpen] = useState(true);
  const [gameType, setGameType] = useState(null);
  const [gone, setGone] = useState(false); // game finished/deleted → hide

  // Spades
  const [players, setPlayers] = useState([]);
  const [trick, setTrick] = useState([]); // [{seat, card}]
  const [bids, setBids] = useState({});
  const [books, setBooks] = useState([0, 0]);
  const [scores, setScores] = useState([0, 0]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  // Checkers / C8 / 4iar
  const [ckState, setCkState] = useState(null);
  const [c8State, setC8State] = useState(null);
  const [fiarBoard, setFiarBoard] = useState(null);
  const [fiarWin, setFiarWin] = useState(null);

  const wsRef = useRef(null);
  const closedRef = useRef(false);

  const applySnapshot = useCallback((data) => {
    if (!data) return;
    setGameType(data.game_type || 'spades');
    if (data.players) setPlayers(data.players);
    if (data.game_type === 'checkers') setCkState(data.state || null);
    else if (data.game_type === 'crazy_eights') setC8State(data.state || null);
    else if (data.game_type === 'four_in_a_row') setFiarBoard(data.state?.board || null);
    else {
      // spades snapshot
      if (data.team1Score !== undefined) setScores([data.team1Score || 0, data.team2Score || 0]);
      if (data.currentPlayer !== undefined) setCurrentPlayer(data.currentPlayer);
    }
  }, []);

  // Initial sanitized snapshot
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/game/${gameId}/spectate`);
        if (res.status === 404) { if (!cancelled) setGone(true); return; }
        const data = await res.json();
        if (!cancelled) applySnapshot(data);
      } catch { /* snapshot is best-effort; WS still paints live moves */ }
    })();
    return () => { cancelled = true; };
  }, [gameId, applySnapshot]);

  // Read-only game WS
  useEffect(() => {
    if (!gameId) return;
    closedRef.current = false;
    let reconnectTimer = null;

    const connect = () => {
      if (closedRef.current) return;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${window.location.host}/api/ws/game/${gameId}?user_id=spectator_${viewerId || 'anon'}`);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        let m; try { m = JSON.parse(event.data); } catch { return; }
        switch (m.type) {
          case 'card_played':
            setGameType((t) => t || 'spades');
            setTrick((prev) => {
              const next = prev.filter((c) => c.seat !== m.seat);
              return [...next, { seat: m.seat, card: m.card }].slice(-4);
            });
            if (m.next_player !== undefined && m.next_player !== null) setCurrentPlayer(m.next_player);
            break;
          case 'bid_submitted':
            setBids((prev) => ({ ...prev, [m.seat]: m.is_blind ? `Blind ${m.bid}` : m.bid }));
            break;
          case 'trick_complete':
            if (m.winner_seat !== undefined && m.winner_seat !== null) {
              setBooks((prev) => {
                const next = [...prev];
                next[m.winner_seat % 2] = (next[m.winner_seat % 2] || 0) + 1;
                return next;
              });
            }
            setTimeout(() => setTrick([]), 900);
            break;
          case 'turn_change':
            if (m.current_player !== undefined) setCurrentPlayer(m.current_player);
            break;
          case 'game_state_update': {
            const gs = m.game_state || {};
            // Public fields only — never read hands/cards from this message.
            if (gs.team1Score !== undefined) setScores([gs.team1Score || 0, gs.team2Score || 0]);
            if (Array.isArray(gs.booksWon)) setBooks(gs.booksWon);
            if (gs.currentPlayer !== undefined) setCurrentPlayer(gs.currentPlayer);
            break;
          }
          case 'checkers_state':
            setGameType('checkers');
            setCkState(m.state || null);
            break;
          case 'c8_state':
            setGameType('crazy_eights');
            setC8State(m.state || null);
            break;
          case 'four_in_a_row_move':
            setGameType('four_in_a_row');
            if (m.board) setFiarBoard(m.board);
            break;
          case 'four_in_a_row_game_over':
            setFiarWin(m.is_draw ? 'Draw!' : 'Winner!');
            setTimeout(() => setGone(true), 8000);
            break;
          default:
            break;
        }
      };
      ws.onclose = () => {
        if (!closedRef.current) reconnectTimer = setTimeout(connect, 3000);
      };
    };

    connect();
    return () => {
      closedRef.current = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current && wsRef.current.close(); } catch {}
    };
  }, [gameId, viewerId]);

  const nameFor = (idxOrId) => {
    if (players && players.length) {
      if (typeof idxOrId === 'number' && players[idxOrId]) return players[idxOrId].name || players[idxOrId].id || `P${idxOrId + 1}`;
      const found = players.find((p) => p.id === idxOrId || p === idxOrId);
      if (found) return found.name || found.id || String(idxOrId);
    }
    return typeof idxOrId === 'number' ? `Player ${idxOrId + 1}` : String(idxOrId || '').slice(0, 10);
  };

  if (!gameId || gone) return null;

  // Winner banners from engine states
  const ckWinner = ckState?.winner;
  const c8Winner = c8State?.winner;

  return (
    <div className="absolute inset-x-0 top-0 z-30 pointer-events-none">
      {/* Toggle chip (always visible) */}
      <div className="flex justify-center mt-10">
        <button
          onClick={() => setOpen((o) => !o)}
          className="pointer-events-auto px-3 py-1 rounded-full bg-black/70 text-white text-xs font-bold border border-white/20 backdrop-blur-sm"
          data-testid="spectator-game-toggle"
        >
          🎮 {open ? 'Hide game' : 'Show game'}
        </button>
      </div>

      {open && (
        <div className="mx-2 mt-1 rounded-2xl bg-black/70 backdrop-blur-md border border-white/10 p-3 pointer-events-auto max-h-[52vh] overflow-y-auto" data-testid="spectator-game-panel">

          {/* ---------- SPADES (default card game view) ---------- */}
          {(gameType === 'spades' || gameType == null) && (
            <div>
              <div className="flex justify-between text-[11px] text-white/80 font-semibold mb-2">
                <span>Team 1: {scores[0]} pts · {books[0]} books</span>
                <span>Team 2: {scores[1]} pts · {books[1]} books</span>
              </div>
              <div className="flex justify-center gap-2 min-h-[48px] items-center">
                {trick.length === 0 && <span className="text-white/40 text-xs">Waiting for the next play…</span>}
                {trick.map((t) => (
                  <div key={t.seat} className="flex flex-col items-center gap-0.5">
                    <MiniCard card={t.card} />
                    <span className="text-[9px] text-white/70">{nameFor(t.seat)}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-1 mt-2">
                {[0, 1, 2, 3].map((seat) => (
                  <div key={seat} className={`rounded-lg px-1 py-0.5 text-center ${currentPlayer === seat ? 'bg-emerald-500/30 border border-emerald-400/60' : 'bg-white/5'}`}>
                    <div className="text-[10px] text-white truncate">{nameFor(seat)}</div>
                    <div className="text-[9px] text-white/60">Bid: {bids[seat] ?? '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ---------- CHECKERS ---------- */}
          {gameType === 'checkers' && ckState?.board && (
            <div>
              <div className="text-center text-[11px] text-white/80 font-semibold mb-1">
                {ckWinner ? `🏆 ${nameFor(ckWinner)} wins!` : ckState.draw ? 'Draw' : `${nameFor(ckState.turn)}'s move`}
              </div>
              <div className="grid grid-cols-8 gap-0 w-fit mx-auto rounded-lg overflow-hidden border border-white/20">
                {ckState.board.map((row, r) => row.map((cell, c) => {
                  const dark = (r + c) % 2 === 1;
                  const isKing = cell && cell === cell.toUpperCase() && cell !== '';
                  const isW = cell && cell.toLowerCase() === 'w';
                  return (
                    <div key={`${r}-${c}`} className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${dark ? 'bg-emerald-900' : 'bg-emerald-200/80'}`}>
                      {cell ? (
                        <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center text-[10px] shadow ${isW ? 'bg-amber-100 text-amber-700' : 'bg-gray-900 text-amber-400 border border-gray-600'}`}>
                          {isKing ? '♛' : ''}
                        </div>
                      ) : null}
                    </div>
                  );
                }))}
              </div>
            </div>
          )}

          {/* ---------- 4 IN A ROW ---------- */}
          {gameType === 'four_in_a_row' && fiarBoard && (
            <div>
              {fiarWin && <div className="text-center text-xs text-yellow-300 font-bold mb-1">{fiarWin}</div>}
              <div className="w-fit mx-auto bg-blue-800 rounded-lg p-1.5">
                {fiarBoard.map((row, r) => (
                  <div key={r} className="flex">
                    {row.map((cell, c) => (
                      <div key={c} className="w-7 h-7 md:w-8 md:h-8 p-0.5">
                        <div className={`w-full h-full rounded-full ${
                          cell === 1 || cell === 'red' || cell === 'R' ? 'bg-red-500'
                          : cell === 2 || cell === 'yellow' || cell === 'Y' ? 'bg-yellow-400'
                          : 'bg-blue-950'
                        }`} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ---------- CRAZY EIGHTS ---------- */}
          {gameType === 'crazy_eights' && c8State && (
            <div>
              <div className="text-center text-[11px] text-white/80 font-semibold mb-2">
                {c8Winner ? `🏆 ${nameFor(c8Winner)} wins!` : `${nameFor(c8State.current_player)}'s turn`}
                {c8State.active_suit && !c8Winner && (
                  <span className={`ml-2 ${RED_SUITS.includes(SUIT_CHAR[c8State.active_suit] || c8State.active_suit) ? 'text-red-400' : 'text-white'}`}>
                    Suit: {SUIT_CHAR[c8State.active_suit] || c8State.active_suit}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-center gap-4">
                <div className="flex flex-col items-center">
                  <MiniCard card={c8State.discard_top} size="lg" />
                  <span className="text-[9px] text-white/60 mt-0.5">Discard</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-16 rounded-md bg-gradient-to-br from-indigo-700 to-indigo-900 border border-white/20 flex items-center justify-center text-white/70 text-xs font-bold">
                    {c8State.stock_count ?? '—'}
                  </div>
                  <span className="text-[9px] text-white/60 mt-0.5">Deck</span>
                </div>
              </div>
              <div className="flex justify-center gap-2 mt-2 flex-wrap">
                {Object.entries(c8State.hand_counts || {}).map(([pid, count]) => (
                  <div key={pid} className={`rounded-lg px-2 py-0.5 ${c8State.current_player === pid ? 'bg-emerald-500/30 border border-emerald-400/60' : 'bg-white/5'}`}>
                    <span className="text-[10px] text-white">{nameFor(pid)}</span>
                    <span className="text-[10px] text-white/60 ml-1">🂠 {count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
